import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Plus,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { EditIconButton } from '../components/shared/EditIconButton';
import { Drawer } from '../components/ui/Drawer';
import { TextAreaField } from '../components/ui/Field';
import { formatDate, timeAgo } from '../lib/format';
import {
  computeProbationKpis,
  ProbationReviewDrawer,
  PROBATION_VERDICT_LABEL,
  PROBATION_VERDICT_TONE,
  STAGE_LABEL,
  STAGE_TONE,
  STAGE_TONE_CLASSES,
  type ProbationDetailResponse,
  type ProbationReviewInitialValues,
  type ProbationReviewRow,
} from '../components/hiring';

const searchSchema = z.object({
  focusReviewId: z.string().optional(),
});

export const Route = createFileRoute('/_authed/hr/probation/$teacherId')({
  validateSearch: searchSchema,
  component: ProbationDetailPage,
});

interface EditTarget {
  reviewId: string;
  initial: ProbationReviewInitialValues;
}

const toInitialValues = (
  row: ProbationReviewRow,
): ProbationReviewInitialValues => ({
  weekNo: row.weekNo,
  periodStart: row.periodStart,
  periodEnd: row.periodEnd,
  verdict: row.verdict,
  ...(row.overallScore !== null ? { overallScore: row.overallScore } : {}),
  ...(row.improvementNotes !== null
    ? { improvementNotes: row.improvementNotes }
    : {}),
  ...(row.progressNotes !== null ? { progressNotes: row.progressNotes } : {}),
  ...(row.concerns !== null ? { concerns: row.concerns } : {}),
});

const formatRatio = (ratio: number | null): string =>
  ratio === null ? '—' : `${ratio}%`;

const formatScore = (score: number | null): string =>
  score === null ? '—' : score.toFixed(1);

function ProbationDetailPage() {
  const { teacherId } = Route.useParams();
  const { focusReviewId } = Route.useSearch();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canConfirmOrTerminate = hasRole('super_admin', 'academic_admin');

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const rowRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  // Track whether we've already auto-scrolled for the current search param so a
  // re-render (e.g. a refetch) doesn't keep yanking the viewport.
  const handledFocusRef = useRef<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['hiring', 'probation', 'teacher', teacherId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ProbationDetailResponse>(
        `/api/hiring/probation/${encodeURIComponent(teacherId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const reviews = detailQuery.data?.reviews ?? [];
  const teacher = detailQuery.data?.teacher;
  const application = detailQuery.data?.application ?? null;

  const kpis = useMemo(() => computeProbationKpis(reviews), [reviews]);

  const nextWeekNo = useMemo(() => {
    if (reviews.length === 0) return 1;
    const highest = Math.max(...reviews.map((r) => r.weekNo));
    return Math.min(12, highest + 1);
  }, [reviews]);

  const sortedReviews = useMemo(
    () => [...reviews].sort((a, b) => a.weekNo - b.weekNo),
    [reviews],
  );

  const showConfirmAction =
    canConfirmOrTerminate &&
    kpis.weeksRecorded >= 12 &&
    kpis.latestVerdict === 'on_track';

  const showTerminateAction =
    canConfirmOrTerminate &&
    (kpis.latestVerdict === 'at_risk' || kpis.latestVerdict === 'terminate');

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['hiring', 'probation', 'teacher', teacherId],
    });
    void queryClient.invalidateQueries({ queryKey: ['hiring', 'probation'] });
  };

  const confirmMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/hiring/probation/${encodeURIComponent(teacherId)}/confirm`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Teacher confirmed off probation.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not confirm teacher.',
        { variant: 'error' },
      ),
  });

  const terminateMutation = useMutation({
    mutationFn: (reason: string) =>
      apiRequest<{ ok: true }>(
        `/api/hiring/probation/${encodeURIComponent(teacherId)}/terminate`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => {
      toast('Teacher terminated.', { variant: 'success' });
      setTerminateOpen(false);
      setTerminateReason('');
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not terminate teacher.',
        { variant: 'error' },
      ),
  });

  const handleConfirm = async () => {
    const ok = await confirm({
      title: 'Confirm this teacher off probation?',
      description:
        'They will be moved off probation and become a regular teacher. All 12 weekly reviews are kept on file.',
      confirmLabel: 'Confirm teacher',
    });
    if (!ok) return;
    confirmMutation.mutate();
  };

  const handleTerminateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const reason = terminateReason.trim();
    if (reason.length === 0) {
      toast('Please add a reason for termination.', { variant: 'error' });
      return;
    }
    terminateMutation.mutate(reason);
  };

  // Auto-scroll + highlight the focused review row once the data arrives.
  // We key off `reviews.length` so a brand-new view (data not yet fetched)
  // still gets the visual hint after the network round-trip resolves.
  useEffect(() => {
    if (!focusReviewId) return;
    if (handledFocusRef.current === focusReviewId) return;
    if (reviews.length === 0) return;
    const exists = reviews.some((r) => r.id === focusReviewId);
    if (!exists) return;
    handledFocusRef.current = focusReviewId;
    // Defer to the next frame so the DOM is laid out before scrolling.
    const raf = window.requestAnimationFrame(() => {
      const node = rowRefs.current.get(focusReviewId);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setHighlightId(focusReviewId);
    });
    const timer = window.setTimeout(() => setHighlightId(null), 2000);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [focusReviewId, reviews]);

  const kpiItems = useMemo<KpiItem[]>(
    () => [
      {
        label: 'Weeks recorded',
        value: kpis.weeksRecorded,
        sub: `of 12`,
      },
      {
        label: 'Current week',
        value: kpis.currentWeekNo === null ? '—' : `#${kpis.currentWeekNo}`,
      },
      {
        label: 'Latest verdict',
        value:
          kpis.latestVerdict === null
            ? '—'
            : PROBATION_VERDICT_LABEL[kpis.latestVerdict],
      },
      {
        label: 'Average score',
        value: formatScore(kpis.averageScore),
      },
      {
        label: 'On-track ratio',
        value: formatRatio(kpis.onTrackRatio),
      },
      {
        label: 'At-risk weeks',
        value: kpis.atRiskWeeks,
      },
    ],
    [kpis],
  );

  const joinedAtDisplay = teacher?.joinedAt ?? teacher?.createdAt ?? null;

  return (
    <ListShell
      title={teacher?.name ?? 'Probation reviews'}
      description={
        teacher
          ? `${teacher.employeeId ?? 'No employee ID'} · ${reviews.length} of 12 weeks recorded`
          : undefined
      }
      primaryAction={
        <Link
          to="/hr/probation"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to probation queue
        </Link>
      }
    >
      <GatedSection
        error={detailQuery.error}
        title="Probation"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detailQuery.data ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <KpiStrip items={kpiItems} />

            {(showConfirmAction || showTerminateAction) && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {showConfirmAction ? (
                  <Button
                    variant="primary"
                    icon={<CheckCircle2 size={14} aria-hidden="true" />}
                    onClick={handleConfirm}
                    loading={confirmMutation.isPending}
                  >
                    Confirm teacher
                  </Button>
                ) : null}
                {showTerminateAction ? (
                  <Button
                    variant="danger"
                    icon={<XCircle size={14} aria-hidden="true" />}
                    onClick={() => setTerminateOpen(true)}
                  >
                    Terminate
                  </Button>
                ) : null}
              </div>
            )}

            <section
              aria-label="Weekly reviews"
              className="space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[color:var(--color-text-muted)]">
                  Next review:{' '}
                  <span className="font-medium text-[color:var(--color-text)]">
                    Week {nextWeekNo}
                  </span>
                </p>
                {nextWeekNo <= 12 ? (
                  <Button
                    icon={<Plus size={14} aria-hidden="true" />}
                    onClick={() => setCreateDrawerOpen(true)}
                  >
                    Add this week's review
                  </Button>
                ) : (
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    All 12 weeks recorded.
                  </p>
                )}
              </div>

              {sortedReviews.length === 0 ? (
                <EmptyState
                  title="No reviews yet"
                  description="Add the first weekly review to get started."
                  action={
                    <Button
                      icon={<Plus size={14} aria-hidden="true" />}
                      onClick={() => setCreateDrawerOpen(true)}
                    >
                      Add review
                    </Button>
                  }
                />
              ) : (
                <ol className="space-y-3">
                  {sortedReviews.map((r) => {
                    const highlighted = highlightId === r.id;
                    return (
                      <li
                        key={r.id}
                        ref={(node) => {
                          if (node) rowRefs.current.set(r.id, node);
                          else rowRefs.current.delete(r.id);
                        }}
                        className={clsx(
                          'rounded-lg border p-4 transition-colors duration-500',
                          highlighted
                            ? 'border-gold-500 bg-gold-50 dark:bg-gold-700/20'
                            : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]',
                        )}
                      >
                        <header className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <p className="font-display text-sm font-semibold">
                              Week {r.weekNo}
                            </p>
                            <span
                              className={clsx(
                                'rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                                STAGE_TONE_CLASSES[
                                  PROBATION_VERDICT_TONE[r.verdict]
                                ],
                              )}
                            >
                              {PROBATION_VERDICT_LABEL[r.verdict]}
                            </span>
                          </div>
                          <EditIconButton
                            label={`Edit week ${r.weekNo} review`}
                            title={`Edit week ${r.weekNo} review`}
                            onClick={() =>
                              setEditTarget({
                                reviewId: r.id,
                                initial: toInitialValues(r),
                              })
                            }
                          />
                        </header>
                        <p className="mt-1 text-xs text-[color:var(--color-text-muted)] tabular-nums">
                          {r.periodStart} → {r.periodEnd} · saved{' '}
                          {timeAgo(r.createdAt)}
                          {r.overallScore !== null
                            ? ` · score ${r.overallScore}`
                            : ''}
                        </p>
                        {r.progressNotes ? (
                          <Section title="Progress notes">
                            {r.progressNotes}
                          </Section>
                        ) : null}
                        {r.improvementNotes ? (
                          <Section title="Areas to improve">
                            {r.improvementNotes}
                          </Section>
                        ) : null}
                        {r.concerns ? (
                          <Section title="Concerns">{r.concerns}</Section>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {application ? (
              <section
                aria-label="Originating application"
                className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
              >
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="inline-flex items-center gap-1.5 font-display text-sm font-semibold">
                    <ClipboardList
                      size={14}
                      aria-hidden="true"
                      className="text-[color:var(--color-text-muted)]"
                    />
                    Originating application
                  </h3>
                  <span
                    className={clsx(
                      'rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                      STAGE_TONE_CLASSES[STAGE_TONE[application.pipelineStage]],
                    )}
                  >
                    {STAGE_LABEL[application.pipelineStage]}
                  </span>
                </header>
                <p className="mt-1 text-sm text-[color:var(--color-text)]">
                  {application.fullName}
                </p>
                <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)] tabular-nums">
                  Submitted {formatDate(application.submittedAt)}
                </p>
                <div className="mt-3">
                  <Link
                    to="/hr/applications/$applicationId"
                    params={{ applicationId: application.id }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 underline hover:no-underline dark:text-gold-300"
                  >
                    Open application
                    <ArrowLeft
                      size={14}
                      aria-hidden="true"
                      className="rotate-180"
                    />
                  </Link>
                </div>
              </section>
            ) : null}

            <ActivityPanel
              targetType="teacher"
              targetId={teacherId}
              title="Activity"
              emptyCopy="No activity recorded for this teacher yet."
            />

            {teacher ? (
              <footer className="mt-4 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
                Teacher record id: <code className="font-mono">{teacher.id}</code>
                {joinedAtDisplay ? (
                  <span>
                    {' '}
                    · joined{' '}
                    <strong className="text-[color:var(--color-text)]">
                      {formatDate(joinedAtDisplay)}
                    </strong>
                  </span>
                ) : null}
              </footer>
            ) : null}
          </div>
        )}
      </GatedSection>

      <ProbationReviewDrawer
        open={createDrawerOpen}
        teacherId={teacherId}
        nextWeekNo={nextWeekNo}
        onClose={() => setCreateDrawerOpen(false)}
        onSaved={() => {
          setCreateDrawerOpen(false);
          invalidate();
        }}
      />

      <ProbationReviewDrawer
        open={editTarget !== null}
        teacherId={teacherId}
        reviewId={editTarget?.reviewId ?? null}
        initialValues={editTarget?.initial}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          invalidate();
        }}
      />

      <Drawer
        open={terminateOpen}
        onClose={() => {
          if (terminateMutation.isPending) return;
          setTerminateOpen(false);
          setTerminateReason('');
        }}
        size="md"
        title="Terminate teacher"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setTerminateOpen(false);
                setTerminateReason('');
              }}
              disabled={terminateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="terminate-form"
              variant="danger"
              loading={terminateMutation.isPending}
            >
              Terminate
            </Button>
          </div>
        }
      >
        <form
          id="terminate-form"
          onSubmit={handleTerminateSubmit}
          className="space-y-4"
          noValidate
        >
          <p className="text-sm text-[color:var(--color-text-muted)]">
            This ends the teacher's employment and closes all probation reviews.
            The reason is recorded on the audit trail and shared with the
            teacher.
          </p>
          <TextAreaField
            label="Reason for termination"
            rows={4}
            required
            placeholder="Explain the decision in a few sentences…"
            value={terminateReason}
            onChange={(e) => setTerminateReason(e.target.value)}
          />
        </form>
      </Drawer>
    </ListShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
        {children}
      </p>
    </div>
  );
}

